import React, { useState, useEffect } from 'react';

function MintLaunchingPage({ config, collectionItems = [], onLaunchComplete }) {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [isSlideOut, setIsSlideOut] = useState(false);
  const [isLaunchComplete, setIsLaunchComplete] = useState(false);

  useEffect(() => {
    if (!config.launch_datetime) return;

    const calculateTimeLeft = () => {
      const launchTime = new Date(config.launch_datetime);
      const now = new Date();
      const difference = launchTime - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        // Trigger launch complete state immediately when countdown reaches zero
        if (!isLaunchComplete) {
          const serverTime = new Date();
          const clientLaunchTime = new Date(config.launch_datetime);
          console.log('🚀 Launch time reached!', { serverTime, clientLaunchTime, difference });
          // Only trigger if server time also confirms launch time has passed
          if (serverTime >= clientLaunchTime) {
            console.log('🎯 Triggering launch complete animation');
            // Show launch complete message immediately
            setIsLaunchComplete(true);
            // Start slide-out after showing the message
            setTimeout(() => {
              setIsSlideOut(true);
            }, 2000);
            // Notify parent component after full transition
            setTimeout(() => {
              if (onLaunchComplete) {
                onLaunchComplete();
              }
            }, 4000);
          }
        }
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [config.launch_datetime]);

  const formatCost = (deal) => {
    if (deal.deal_type === 'dcl_wearable') {
      return deal.dcl_price_mana > 0 ? `${deal.dcl_price_mana} MANA` : 'Free Mint';
    }
    return 'Special Deal';
  };

  const formatLimits = (deal) => {
    const limits = [];
    if (deal.limit_per_wallet && deal.limit_per_wallet > 0) {
      limits.push(`${deal.limit_per_wallet} per wallet`);
    }
    if (deal.limit_per_token_enabled && deal.limit_per_token && deal.limit_per_token > 0) {
      limits.push(`${deal.limit_per_token} per token`);
    }
    return limits.length > 0 ? limits.join(' • ') : 'No limits';
  };

  // Helper function to generate marketplace URL for collection items
  const getMarketplaceUrl = (collection, itemId) => {
    if (!collection) return null;
    
    // If we have a specific item ID, link to that item
    if (itemId) {
      return `https://decentraland.org/marketplace/contracts/${collection}/items/${itemId}`;
    }
    
    // Otherwise, link to the collection browse page
    return `https://decentraland.org/marketplace/browse?assetType=item&vendor=decentraland&contracts=${collection}`;
  };

  // Helper function to get collection item info by collection address and item ID
  const getCollectionItemInfo = (collectionAddress, itemId) => {
    if (!collectionAddress) {
      return {
        name: 'Unknown Item',
        image: null
      };
    }

    if (!itemId) {
      // If no item_id specified, return collection info
      const collection = collectionItems.find(
        item => item.collection_address.toLowerCase() === collectionAddress.toLowerCase()
      );
      return {
        name: collection?.title || `${collectionAddress.slice(0, 6)}...${collectionAddress.slice(-4)}`,
        image: collection?.image || null
      };
    }
    
    // Find specific item by collection address and item_id
    // Try both urn_item_id and dcl_item_id for compatibility
    const collectionItem = collectionItems.find(
      item => item.collection_address.toLowerCase() === collectionAddress.toLowerCase() && 
              (item.urn_item_id === itemId || item.dcl_item_id === itemId || item.token_id === itemId)
    );
    
    return {
      name: collectionItem?.title || `Item #${itemId}`,
      image: collectionItem?.image || null
    };
  };

  // Get free mint recipe info for display - show token gate requirements, not rewards
  const getFreeMintInfo = (recipe) => {
    // For free mint display, we want to show what NFTs are REQUIRED (token gates), not what you GET (rewards)
    // Use token_gate_urn_requirements for display since it has the actual urn_item_id values
    const displayRequirements = recipe.token_gate_urn_requirements || [];
    
    if (displayRequirements.length === 0) {
      return {
        name: recipe.title,
        image: null,
        allocation: `${recipe.limit_per_token || 2} ${(recipe.limit_per_token || 2) === 1 ? 'claim' : 'claims'} per token`,
        requirements: []
      };
    }

    // Get info about the first required NFT for the main display
    const firstRequirement = displayRequirements[0];
    const itemInfo = getCollectionItemInfo(firstRequirement.collection, firstRequirement.urn_item_id);
    
    // Create list of all requirements for display using urn_item_id
    const requirements = displayRequirements.map(req => {
      const reqInfo = getCollectionItemInfo(req.collection, req.urn_item_id);
      return {
        name: reqInfo.name || `Item #${req.urn_item_id}`,
        image: reqInfo.image,
        quantity: req.quantity || 1
      };
    });
    
    
    return {
      name: itemInfo.name || recipe.title,
      image: itemInfo.image,
      allocation: `${recipe.limit_per_token || 2} claims per token`,
      requirements: requirements
    };
  };

  // Get rewards for display - handles both single items and RNG pools (works for both deals and recipes)
  const getRewards = (item) => {
    // Handle recipes with RNG
    if (item.rng_enabled && item.rng_pool && item.rng_pool.length > 0 && item.rewards && item.rewards.length > 0) {
      return item.rewards.map((reward, index) => {
        const itemInfo = getCollectionItemInfo(reward.collection, reward.item_id);
        return {
          collection: reward.collection,
          item_id: reward.item_id,
          name: itemInfo.name && itemInfo.name !== `Item #${reward.item_id}` ? itemInfo.name : reward.name,
          image: itemInfo.image || reward.image,
          chance: item.rng_pool[index] || 0
        };
      });
    }
    
    // Handle deals with RNG
    if (item.rng_enabled && item.rng_pool && item.rng_pool.length > 0 && item.deal_items && item.deal_items.length > 0) {
      // Correlate deal_items with rng_pool chances by index
      return item.deal_items.map((dealItem, index) => {
        // Get proper name from collection items using the deal item's data
        const itemInfo = getCollectionItemInfo(dealItem.dcl_collection_address, dealItem.dcl_item_id);
        
        // Create a better fallback name for RNG items
        const fallbackName = dealItem.name || `${item.deal_name} - Variant ${index + 1}`;
        
        return {
          dcl_collection_address: dealItem.dcl_collection_address,
          dcl_item_id: dealItem.dcl_item_id,
          name: itemInfo.name && itemInfo.name !== `Item #${dealItem.dcl_item_id}` ? itemInfo.name : fallbackName,
          image: itemInfo.image || dealItem.deal_image, // Use deal_image from deal_items as fallback
          chance: item.rng_pool[index] || 0 // Get corresponding chance from rng_pool
        };
      });
    } 
    
    // Handle single reward recipes
    if (item.rewards && item.rewards.length > 0) {
      return item.rewards.map(reward => {
        const itemInfo = getCollectionItemInfo(reward.collection, reward.item_id);
        return {
          collection: reward.collection,
          item_id: reward.item_id,
          image: itemInfo.image || reward.image,
          name: itemInfo.name && itemInfo.name !== `Item #${reward.item_id}` ? itemInfo.name : reward.name,
          chance: null
        };
      });
    }
    
    // Handle single item deals (DCL wearables)
    if (item.deal_type === 'dcl_wearable') {
      const itemInfo = getCollectionItemInfo(item.dcl_collection_address, item.dcl_item_id);
      return [{
        dcl_collection_address: item.dcl_collection_address,
        dcl_item_id: item.dcl_item_id,
        image: itemInfo.image || item.deal_image,
        name: itemInfo.name && itemInfo.name !== `Item #${item.dcl_item_id}` ? itemInfo.name : item.deal_name,
        chance: null
      }];
    }
    
    return [];
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600;700;800&display=swap');
        
        .mint-launching-page {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          margin: 0;
          padding: 0;
          font-family: 'Inter', sans-serif;
          background: linear-gradient(-45deg, #000000, #1a1a1a, #2d2d2d, #000000);
          background-size: 400% 400%;
          animation: gradientShift 15s ease infinite;
          color: white;
          overflow-x: hidden;
          overflow-y: auto;
          z-index: 1000;
          transition: transform 1.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 1.5s ease;
        }
        
        .mint-launching-page.slide-out {
          transform: translateY(-100%);
          opacity: 0;
        }
        
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        .mint-launching-particles {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          overflow: hidden;
        }
        
        .mint-launching-particle {
          position: absolute;
          background: linear-gradient(180deg, 
            #8b0000 0%,
            #dc143c 15%,
            #ff4500 35%,
            #ff6347 55%,
            #ff8c00 75%,
            #ffd700 100%
          );
          border-radius: 50%;
          opacity: 0.6;
          animation: float 6s ease-in-out infinite;
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.6; }
          50% { transform: translateY(-20px) rotate(180deg); opacity: 1; }
        }
        
        .mint-launching-container {
          min-height: 85vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          position: relative;
          padding: 0.5rem 0;
        }
        
        .mint-launching-badge {
          display: inline-block;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 50px;
          padding: 0.5rem 1.5rem;
          margin-bottom: 0.5rem;
          font-size: 0.9rem;
          font-weight: 600;
          letter-spacing: 2px;
          text-transform: uppercase;
          animation: pulse 2s ease-in-out infinite;
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.05); opacity: 1; }
        }
        
        .mint-launching-title {
          font-size: clamp(2.5rem, 8vw, 4rem);
          font-weight: 800;
          margin-bottom: 0.25rem;
          background: linear-gradient(45deg, #ff0000, #ff8c00, #ffd700, #32cd32, #00bfff, #8a2be2, #ff1493);
          background-size: 400% 400%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: rainbowFlow 3s ease-in-out infinite;
          text-shadow: 0 0 30px rgba(255, 255, 255, 0.5);
          text-align: center;
          text-transform: uppercase;
        }
        
        @keyframes rainbowFlow {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        .mint-launching-subtitle {
          font-size: clamp(1rem, 3vw, 1.5rem);
          color: rgba(255, 255, 255, 0.8);
          margin-bottom: 1rem;
          text-align: center;
          animation: subtitleFade 3s ease-in-out infinite;
        }
        
        @keyframes subtitleFade {
          0%, 100% { opacity: 0.8; }
          50% { opacity: 0.5; }
        }
        
        .countdown-container {
          margin: 0.5rem 0;
          display: flex;
          justify-content: center;
        }
        
        .countdown-timer {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1.5rem;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(15px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 20px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
          transition: all 0.5s ease;
        }
        
        .countdown-timer.countdown-complete {
          background: rgba(0, 255, 128, 0.2);
          border-color: rgba(0, 255, 128, 0.5);
          box-shadow: 0 8px 32px rgba(0, 255, 128, 0.3);
          animation: countdownComplete 1s ease-in-out;
        }
        
        @keyframes countdownComplete {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
        
        .launch-live-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1001;
          pointer-events: none;
          opacity: 1;
          background: rgba(0, 0, 0, 0.5);
          animation: launchOverlayPulse 0.5s ease-in-out infinite;
        }
        
        .launch-live-message {
          animation: launchLiveAppear 0.5s ease-out, rainbowFlow 3s ease-in-out infinite, launchLivePulse 1s ease-in-out infinite 0.5s;
        }
        
        @keyframes launchOverlayPulse {
          0% { 
            background: rgba(0, 0, 0, 0.3); 
          }
          50% { 
            background: rgba(0, 0, 0, 0.7); 
          }
          100% { 
            background: rgba(0, 0, 0, 0.3); 
          }
        }
        
        @keyframes launchLiveAppear {
          0% { 
            opacity: 0; 
            transform: scale(0.8) translateY(20px); 
          }
          100% { 
            opacity: 1; 
            transform: scale(1) translateY(0); 
          }
        }
        
        @keyframes launchLivePulse {
          0%, 100% { 
            transform: scale(1); 
            text-shadow: 0 0 30px rgba(255, 255, 255, 0.5);
          }
          50% { 
            transform: scale(1.02); 
            text-shadow: 0 0 40px rgba(255, 255, 255, 0.8);
          }
        }
        
        @keyframes rainbowFlow {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        .countdown-unit {
          display: flex;
          flex-direction: column;
          align-items: center;
          min-width: 80px;
        }
        
        .countdown-number {
          font-size: clamp(2rem, 6vw, 3.5rem);
          font-weight: 900;
          background: linear-gradient(45deg, #ff0000, #ff8c00, #ffd700, #32cd32, #00bfff, #8a2be2, #ff1493);
          background-size: 400% 400%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: rainbowFlow 3s ease-in-out infinite;
          text-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
        }
        
        .countdown-label {
          font-size: 0.9rem;
          color: rgba(255, 255, 255, 0.7);
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-top: 0.5rem;
        }
        
        .countdown-separator {
          font-size: clamp(1.5rem, 4vw, 2.5rem);
          color: rgba(255, 255, 255, 0.5);
          animation: separatorBlink 2s ease-in-out infinite;
        }
        
        @keyframes separatorBlink {
          0%, 50% { opacity: 0.5; }
          25%, 75% { opacity: 1; }
        }
        
        .deals-showcase {
          width: 100%;
          max-width: 1200px;
        }
        
        .showcase-title {
          font-size: clamp(1.5rem, 4vw, 2.5rem);
          font-weight: 700;
          color: white;
          margin-bottom: 2rem;
          text-align: center;
          text-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
          text-transform: uppercase;
        }
        
        .deals-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 1.5rem;
          margin-top: 1.5rem;
        }
        
        .deal-card {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(15px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 20px;
          padding: 1.5rem;
          transition: all 0.3s ease;
          animation: dealCardSlideIn 0.8s ease-out forwards;
          opacity: 0;
          transform: translateY(50px);
        }
        
        .deal-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
          border-color: rgba(255, 255, 255, 0.3);
        }
        
        @keyframes dealCardSlideIn {
          0% {
            opacity: 0;
            transform: translateY(50px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .deal-name {
          font-size: 1.5rem;
          font-weight: 700;
          color: white;
          margin-bottom: 1rem;
          text-align: center;
          text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
        }
        
        .deal-cost, .deal-limits {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          padding: 0.75rem 0;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .cost-label, .limits-label {
          color: rgba(255, 255, 255, 0.7);
          font-size: 1rem;
          font-weight: 600;
        }
        
        .cost-value {
          color: #00ff80;
          font-weight: 700;
          font-size: 1.2rem;
          text-shadow: 0 0 10px rgba(0, 255, 128, 0.3);
        }
        
        .limits-value {
          color: #ffd700;
          font-weight: 600;
          font-size: 1rem;
          text-shadow: 0 0 10px rgba(255, 215, 0, 0.3);
        }

        .free-claims-section {
          margin-top: 1rem;
          padding-top: 1rem;
        }

        .free-claims-title {
          color: rgba(255, 255, 255, 0.9);
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: 1rem;
          text-align: center;
        }

        .free-claim-item {
          display: flex;
          align-items: center;
          padding: 0.5rem;
          background: rgba(0, 255, 128, 0.05);
          border: 1px solid rgba(0, 255, 128, 0.2);
          border-radius: 8px;
          margin-bottom: 0.5rem;
          transition: all 0.3s ease;
          cursor: pointer;
          text-decoration: none;
          color: inherit;
        }

        .free-claim-item:hover {
          background: rgba(0, 255, 128, 0.08);
          border-color: rgba(0, 255, 128, 0.3);
          transform: translateX(5px);
        }

        .free-claim-image {
          width: 32px;
          height: 32px;
          object-fit: cover;
          border-radius: 6px;
          border: 1px solid rgba(255, 255, 255, 0.2);
          margin-right: 0.75rem;
          flex-shrink: 0;
        }

        .free-claim-info {
          flex: 1;
        }

        .free-claim-name {
          color: white;
          font-weight: 600;
          font-size: 0.85rem;
          margin-bottom: 0.15rem;
        }

        .free-claim-allocation {
          color: #00ff80;
          font-size: 0.75rem;
          font-weight: 500;
        }
        
        .rewards-section {
          margin-top: 1rem;
          margin-right: 1.5rem;
        }
        
        .rewards-title {
          color: rgba(255, 255, 255, 0.9);
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: 1rem;
          text-align: center;
        }
        
        .rewards-grid {
          display: grid;
          gap: 1rem;
          position: relative;
        }
        
        /* Dynamic grid columns based on number of items */
        .rewards-grid.single-item {
          grid-template-columns: 1fr;
        }
        
        /* Special styling for single reward items - make them much bigger */
        .rewards-grid.single-item .reward-card {
          padding: 1rem;
          max-width: 350px;
          margin: 0 auto;
        }
        
        .rewards-grid.single-item .reward-image {
          width: 90px;
          height: 90px;
          margin-bottom: 0.75rem;
        }
        
        .rewards-grid.single-item .reward-name {
          font-size: 1.2rem;
          margin-bottom: 0.75rem;
          text-align: center;
        }
        
        .rewards-grid.single-item .reward-chance {
          font-size: 0.8rem;
          padding: 0.4rem 0.8rem;
        }
        
        .rewards-grid.two-items {
          grid-template-columns: 1fr auto 1fr; /* card, small OR, card */
        }
        
        .rewards-grid.three-items {
          grid-template-columns: 1fr auto 1fr auto 1fr; /* card, small OR, card, small OR, card */
        }
        
        .rewards-grid.four-plus-items {
          grid-template-columns: 1fr auto 1fr; /* card, small OR, card for each row */
        }
        
        .reward-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 1.2rem;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          transition: all 0.3s ease;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        
        .reward-card:hover {
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(0, 191, 255, 0.3);
          transform: translateY(-5px);
          box-shadow: 0 10px 30px rgba(0, 191, 255, 0.2);
        }
        
        .reward-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(45deg, transparent, rgba(0, 191, 255, 0.1), transparent);
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
        }
        
        .reward-card:hover::before {
          opacity: 1;
        }
        
        .reward-image {
          width: 80px;
          height: 80px;
          object-fit: cover;
          border-radius: 10px;
          border: 2px solid rgba(255, 255, 255, 0.2);
          margin-bottom: 0.5rem;
          transition: transform 0.3s ease;
        }
        
        .reward-card:hover .reward-image {
          transform: scale(1.1);
        }
        
        .reward-name {
          color: white;
          font-weight: 700;
          font-size: 1rem;
          margin-bottom: 0.5rem;
          line-height: 1.3;
          text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
        }
        
        .reward-chance {
          display: inline-block;
          background: linear-gradient(45deg, #ff0000, #ff8c00, #ffd700, #32cd32, #00bfff, #8a2be2, #ff1493);
          background-size: 400% 400%;
          color: #fff;
          padding: 0.25rem 0.5rem;
          border-radius: 1rem;
          font-size: 0.65rem;
          font-weight: bold;
          text-shadow: none;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
          animation: rainbowFlow 3s ease-in-out infinite;
          transition: all 0.3s ease;
        }
        
        .reward-chance:hover {
          transform: translateY(-1px) scale(1.05);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);
        }
        
        @keyframes rainbowFlow {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        .rng-separator {
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          font-size: 1.2rem;
          font-weight: bold;
          color: #fff;
          text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
          padding: 1rem 0.25rem;
          position: relative;
          min-height: 80px;
          min-width: 40px;
        }
        
        .rng-separator.horizontal {
          grid-column: 1 / -1;
          margin: 1rem 0;
        }
        
        .rng-separator.horizontal::before,
        .rng-separator.horizontal::after {
          content: '';
          position: absolute;
          top: 50%;
          width: 35%;
          height: 2px;
          background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.4), transparent);
        }
        
        .rng-separator.horizontal::before {
          left: 5%;
        }
        
        .rng-separator.horizontal::after {
          right: 5%;
        }
        
        .rng-separator.vertical {
          /* Remove writing-mode to keep text horizontal */
          background: linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.05), transparent);
        }
        
        /* New vertical OR separator for full-width RNG tiles */
        .rng-separator-vertical {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem 0;
          font-weight: bold;
          font-size: 1.1rem;
          color: rgba(255, 255, 255, 0.8);
          text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
        }
        
        /* Full-width reward tile for RNG */
        .reward-tile-full {
          display: flex;
          align-items: center;
          padding: 1rem;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          transition: all 0.3s ease;
          width: 100%;
        }
        
        .reward-tile-full:hover {
          background: rgba(255, 255, 255, 0.05);
          border-color: rgba(255, 255, 255, 0.2);
          transform: translateY(-2px);
        }
        
        .reward-tile-image {
          width: 80px;
          height: 80px;
          object-fit: cover;
          border-radius: 6px;
          margin-right: 1rem;
          flex-shrink: 0;
        }
        
        .reward-tile-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          justify-content: center;
          align-items: center;
          text-align: center;
        }
        
        .reward-tile-title-row {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          width: 100%;
        }
        
        /* Special styling for RNG tile reward names to match single reward size */
        .reward-tile-full .reward-name {
          font-size: 1.1rem;
          margin-bottom: 0;
        }
        
        /* Special styling for RNG tile reward chance labels - inline with title */
        .reward-tile-full .reward-chance {
          padding: 0.3rem 0.6rem;
          font-size: 0.7rem;
          border-radius: 0.8rem;
          min-height: 1.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          white-space: nowrap;
          min-width: 90px;
          flex-shrink: 0;
        }
        
        /* RNG vertical list container */
        .rewards-grid.rng-vertical-list {
          display: flex;
          flex-direction: column;
          gap: 0;
        }
        
        /* RNG quad layout for 4+ rewards */
        .rewards-grid.rng-quad-layout {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        
        .rng-quad-row {
          display: flex;
          justify-content: space-around;
          align-items: center;
          gap: 0.5rem;
        }
        
        .rng-quad-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          flex: 1;
          max-width: 140px;
          text-align: center;
        }
        
        .rng-quad-item .reward-image {
          width: 70px;
          height: 70px;
          object-fit: cover;
          border-radius: 8px;
          margin-bottom: 0.5rem;
        }
        
        .rng-quad-item .reward-name {
          font-size: 0.9rem;
          font-weight: 600;
          color: white;
          margin-bottom: 0.3rem;
          line-height: 1.2;
        }
        
        .rng-quad-item .reward-chance {
          padding: 0.25rem 0.5rem;
          font-size: 0.65rem;
          border-radius: 0.6rem;
          min-height: 1.2rem;
          display: flex;
          align-items: center;
          justify-content: center;
          white-space: nowrap;
          min-width: 70px;
        }
        
        .rng-quad-or {
          font-size: 1rem;
          font-weight: bold;
          color: #fff;
          text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
          margin: 0 0.5rem;
        }
        
        .rng-quad-or-center {
          font-size: 1.1rem;
          font-weight: bold;
          color: #fff;
          text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
          text-align: center;
          margin: 1rem 0;
        }
        
        /* Responsive adjustments */
        @media (max-width: 768px) {
          .rewards-grid.two-items,
          .rewards-grid.three-items,
          .rewards-grid.four-plus-items {
            --reward-cols: 1;
          }
          
          .reward-card {
            padding: 1rem;
          }
          
          .reward-image {
            width: 90px;
            height: 90px;
          }
          
          /* Single reward mobile adjustments */
          .rewards-grid.single-item .reward-image {
            width: 80px;
            height: 80px;
          }
          
          .rewards-grid.single-item .reward-name {
            font-size: 1.3rem;
          }
          
          .rewards-grid.single-item .reward-card {
            padding: 1.5rem;
            max-width: 300px;
          }
          
          /* RNG tile mobile adjustments */
          .reward-tile-image {
            width: 80px;
            height: 80px;
            margin-right: 0.75rem;
          }
          
          .reward-tile-full .reward-name {
            font-size: 1.3rem;
          }
          
          .reward-tile-full .reward-chance {
            padding: 0.25rem 0.5rem;
            font-size: 0.65rem;
            min-height: 1.4rem;
            min-width: 80px;
            white-space: nowrap;
          }
        }
        
        @media (max-width: 480px) {
          .rewards-grid {
            --reward-cols: 1 !important;
          }
        }
        
        .mint-launching-footer {
          margin-top: 0.5rem;
          text-align: center;
          color: rgba(255, 255, 255, 0.6);
          padding-bottom: 0.5rem;
        }
        
        .mint-launching-footer p {
          font-size: 1rem;
          margin-bottom: 0.25rem;
        }
        
        .footer-logo {
          font-size: 1.5rem;
          font-weight: 700;
          background: linear-gradient(45deg, #ff0080, #00bfff);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        /* Responsive Design */
        @media (max-width: 768px) {
          .mint-launching-container {
            padding: 0.5rem;
          }
          
          .countdown-timer {
            gap: 0.5rem;
            padding: 1rem;
          }
          
          .countdown-unit {
            min-width: 60px;
          }
          
          .deals-grid {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
          
          .deal-card {
            padding: 1rem;
          }
        }
        
        @media (max-width: 480px) {
          .countdown-timer {
            flex-wrap: wrap;
            gap: 0.25rem;
          }
          
          .countdown-separator {
            display: none;
          }
          
          .countdown-unit {
            min-width: 50px;
          }
          
          .deals-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
      
      <div className={`mint-launching-page ${isSlideOut ? 'slide-out' : ''}`}>
        <div className="mint-launching-particles" id="mint-launching-particles"></div>
        
        <div className="mint-launching-container">
          {/* Header */}
          <h1 className="mint-launching-title">
            {config.title || 'Mint Launching Shortly'}
          </h1>
          {config.subtitle && (
            <p className="mint-launching-subtitle">
              {config.subtitle}
            </p>
          )}

          {/* Countdown Timer */}
          <div className="countdown-container">
            <div className={`countdown-timer ${isLaunchComplete ? 'countdown-complete' : ''}`}>
              <div className="countdown-unit">
                <div className="countdown-number">{String(timeLeft.days).padStart(2, '0')}</div>
                <div className="countdown-label">Days</div>
              </div>
              <div className="countdown-separator">:</div>
              <div className="countdown-unit">
                <div className="countdown-number">{String(timeLeft.hours).padStart(2, '0')}</div>
                <div className="countdown-label">Hours</div>
              </div>
              <div className="countdown-separator">:</div>
              <div className="countdown-unit">
                <div className="countdown-number">{String(timeLeft.minutes).padStart(2, '0')}</div>
                <div className="countdown-label">Minutes</div>
              </div>
              <div className="countdown-separator">:</div>
              <div className="countdown-unit">
                <div className="countdown-number">{String(timeLeft.seconds).padStart(2, '0')}</div>
                <div className="countdown-label">Seconds</div>
              </div>
            </div>
          </div>

          {/* Deals and Recipe Showcase */}
          {((config.deals && config.deals.length > 0) || (config.freeMintRecipes && config.freeMintRecipes.length > 0)) && (
            <div className="deals-showcase">
              <h2 className="showcase-title">Items Minting</h2>
              <div className="deals-grid">
                {/* Regular Deals */}
                {config.deals && config.deals.map((deal, index) => {
                  const rewards = getRewards(deal);
                  // Get recipes pinned to this specific deal
                  const pinnedRecipes = config.freeMintRecipes?.filter(recipe => {
                    const mapping = config.recipe_to_deals_mapping || {};
                    const dealIds = mapping[recipe.id] || [];
                    return dealIds.includes(deal.id);
                  }) || [];
                  
                  return (
                    <div key={`deal-${deal.id}`} className="deal-card" style={{ animationDelay: `${index * 0.2}s` }}>
                      <h3 className="deal-name">{deal.deal_name}</h3>
                      
                      <div className="deal-cost">
                        <span className="cost-label">Mint Cost:</span>
                        <span className="cost-value">{formatCost(deal)}</span>
                      </div>
                      
                      <div className="deal-limits">
                        <span className="limits-label">Limits:</span>
                        <span className="limits-value">{formatLimits(deal)}</span>
                      </div>
                      
                      {/* Free Claims Section - only for recipes pinned to this deal */}
                      {pinnedRecipes.length > 0 && (
                        <div className="deal-cost">
                          <span className="cost-label">Free Claims:</span>
                          <span className="cost-value" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                            {pinnedRecipes.map((recipe, index) => {
                              const freeMintInfo = getFreeMintInfo(recipe);
                              
                              return (
                                <span key={recipe.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  {freeMintInfo.requirements.map((req, i) => {
                                    const marketplaceUrl = getMarketplaceUrl(
                                      recipe.token_gate_urn_requirements?.[i]?.collection || 
                                      recipe.token_gate_requirements?.[i]?.collection,
                                      recipe.token_gate_urn_requirements?.[i]?.urn_item_id || 
                                      recipe.token_gate_requirements?.[i]?.item_id
                                    );
                                    
                                    return (
                                      <span key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                        {marketplaceUrl ? (
                                          <a 
                                            href={marketplaceUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '0.75rem',
                                              color: 'inherit',
                                              textDecoration: 'none'
                                            }}
                                          >
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', paddingRight: '1rem' }}>
                                              <span style={{ fontSize: '0.9rem' }}>
                                                Requires: {req.quantity}x {req.name}
                                              </span>
                                              <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>
                                                {recipe.limit_per_token === 1 ? '1 claim per token' : `${recipe.limit_per_token || 2} claims per token`}
                                              </span>
                                            </div>
                                            {req.image && (
                                              <img 
                                                src={req.image} 
                                                alt={req.name}
                                                style={{
                                                  width: '40px',
                                                  height: '40px',
                                                  objectFit: 'cover',
                                                  borderRadius: '4px'
                                                }}
                                              />
                                            )}
                                          </a>
                                        ) : (
                                          <>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', paddingRight: '1rem' }}>
                                              <span style={{ fontSize: '0.9rem' }}>
                                                Requires: {req.quantity}x {req.name}
                                              </span>
                                              <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>
                                                {recipe.limit_per_token === 1 ? '1 claim per token' : `${recipe.limit_per_token || 2} claims per token`}
                                              </span>
                                            </div>
                                            {req.image && (
                                              <img 
                                                src={req.image} 
                                                alt={req.name}
                                                style={{
                                                  width: '40px',
                                                  height: '40px',
                                                  objectFit: 'cover',
                                                  borderRadius: '4px'
                                                }}
                                              />
                                            )}
                                          </>
                                        )}
                                        {i < freeMintInfo.requirements.length - 1 && <span>, </span>}
                                      </span>
                                    );
                                  })}
                                  {index < pinnedRecipes.length - 1 && <span style={{ margin: '0 0.5rem' }}>|</span>}
                                </span>
                              );
                            })}
                          </span>
                        </div>
                      )}
                      
                      {/* Rewards Section */}
                      {rewards.length > 0 && (
                        <div className="rewards-section">
                          <div className="rewards-title">
                            {deal.rng_enabled ? 'Possible Rewards:' : 'Reward:'}
                          </div>
                          <div className={`rewards-grid ${
                            rewards.length === 1 ? 'single-item' :
                            deal.rng_enabled ? 'rng-quad-layout' :
                            rewards.length === 2 ? 'two-items' :
                            rewards.length === 3 ? 'three-items' :
                            'four-plus-items'
                          }`}>
                            {(() => {
                              if (deal.rng_enabled && rewards.length > 1) {
                                if (rewards.length >= 4) {
                                  // Quad layout for 4+ rewards
                                  return (
                                    <div>
                                      {/* Top row */}
                                      <div className="rng-quad-row">
                                        <div className="rng-quad-item">
                                          {rewards[0].image && (
                                            <img 
                                              src={rewards[0].image} 
                                              alt={rewards[0].name || deal.deal_name}
                                              className="reward-image"
                                            />
                                          )}
                                          <div className="reward-name">{rewards[0].name || deal.deal_name}</div>
                                          <div className="reward-chance">
                                            🎲 {rewards[0].chance}% Chance
                                          </div>
                                        </div>
                                        
                                        <div className="rng-quad-or">OR</div>
                                        
                                        <div className="rng-quad-item">
                                          {rewards[1].image && (
                                            <img 
                                              src={rewards[1].image} 
                                              alt={rewards[1].name || deal.deal_name}
                                              className="reward-image"
                                            />
                                          )}
                                          <div className="reward-name">{rewards[1].name || deal.deal_name}</div>
                                          <div className="reward-chance">
                                            🎲 {rewards[1].chance}% Chance
                                          </div>
                                        </div>
                                      </div>
                                      
                                      {/* ORs above bottom row */}
                                      <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-around', 
                                        alignItems: 'center',
                                        margin: '1rem 0',
                                        position: 'relative'
                                      }}>
                                        <div style={{ flex: 1, textAlign: 'center' }}>
                                          <div className="rng-quad-or">OR</div>
                                        </div>
                                        <div style={{ flex: 1, textAlign: 'center' }}>
                                          <div className="rng-quad-or">OR</div>
                                        </div>
                                      </div>
                                      
                                      {/* Bottom row */}
                                      <div className="rng-quad-row">
                                        <div className="rng-quad-item">
                                          {rewards[2].image && (
                                            <img 
                                              src={rewards[2].image} 
                                              alt={rewards[2].name || deal.deal_name}
                                              className="reward-image"
                                            />
                                          )}
                                          <div className="reward-name">{rewards[2].name || deal.deal_name}</div>
                                          <div className="reward-chance">
                                            🎲 {rewards[2].chance}% Chance
                                          </div>
                                        </div>
                                        
                                        <div className="rng-quad-or">OR</div>
                                        
                                        <div className="rng-quad-item">
                                          {rewards[3].image && (
                                            <img 
                                              src={rewards[3].image} 
                                              alt={rewards[3].name || deal.deal_name}
                                              className="reward-image"
                                            />
                                          )}
                                          <div className="reward-name">{rewards[3].name || deal.deal_name}</div>
                                          <div className="reward-chance">
                                            🎲 {rewards[3].chance}% Chance
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                } else {
                                  // Vertical list for 2-3 rewards - but use quad layout for 2 items
                                  if (rewards.length === 2) {
                                    return (
                                      <div>
                                        {/* Single row with two items */}
                                        <div className="rng-quad-row">
                                          <div className="rng-quad-item">
                                            {rewards[0].image && (
                                              <img 
                                                src={rewards[0].image} 
                                                alt={rewards[0].name || deal.deal_name}
                                                className="reward-image"
                                                style={{
                                                  width: '70px',
                                                  height: '70px'
                                                }}
                                              />
                                            )}
                                            <div className="reward-name" style={{ fontSize: '0.9rem' }}>
                                              {rewards[0].name || deal.deal_name}
                                            </div>
                                            <div className="reward-chance" style={{ 
                                              fontSize: '0.65rem', 
                                              minWidth: '70px',
                                              whiteSpace: 'nowrap',
                                              flexShrink: 0,
                                              padding: '0.3rem 0.6rem'
                                            }}>
                                              🎲 {rewards[0].chance}% Chance
                                            </div>
                                          </div>
                                          
                                          <div className="rng-quad-or">OR</div>
                                          
                                          <div className="rng-quad-item">
                                            {rewards[1].image && (
                                              <img 
                                                src={rewards[1].image} 
                                                alt={rewards[1].name || deal.deal_name}
                                                className="reward-image"
                                                style={{
                                                  width: '70px',
                                                  height: '70px'
                                                }}
                                              />
                                            )}
                                            <div className="reward-name" style={{ fontSize: '0.9rem' }}>
                                              {rewards[1].name || deal.deal_name}
                                            </div>
                                            <div className="reward-chance" style={{ 
                                              fontSize: '0.65rem', 
                                              minWidth: '70px',
                                              whiteSpace: 'nowrap',
                                              flexShrink: 0,
                                              padding: '0.3rem 0.6rem'
                                            }}>
                                              🎲 {rewards[1].chance}% Chance
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  } else {
                                    // Original vertical list for 3+ rewards
                                    const elements = [];
                                    rewards.forEach((reward, i) => {
                                    // Add reward tile
                                    elements.push(
                                      <div key={i} className="reward-tile-full">
                                        {reward.image && (
                                          <img 
                                            src={reward.image} 
                                            alt={reward.name || deal.deal_name}
                                            className="reward-tile-image"
                                          />
                                        )}
                                        <div className="reward-tile-content">
                                          <div className="reward-tile-title-row">
                                            <div className="reward-name">
                                              {reward.name || deal.deal_name}
                                            </div>
                                            <div className="reward-chance">
                                              🎲 {reward.chance}% Chance
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                    
                                    // Add OR separator (except after last item)
                                    if (i < rewards.length - 1) {
                                      elements.push(
                                        <div key={`or-${i}`} className="rng-separator-vertical">OR</div>
                                      );
                                    }
                                    });
                                    return elements;
                                  }
                                }
                              } else if (rewards.length === 1) {
                                // Single item - no separators needed
                                const reward = rewards[0];
                                return (
                                  <div className="reward-card">
                                    {reward.image && (
                                      <img 
                                        src={reward.image} 
                                        alt={reward.name || deal.deal_name}
                                        className="reward-image"
                                      />
                                    )}
                                    <div className="reward-name">
                                      {reward.name || deal.deal_name}
                                    </div>
                                    {deal.rng_enabled && reward.chance && (
                                      <div className="reward-chance">
                                        🎲 {reward.chance}% Chance
                                      </div>
                                    )}
                                  </div>
                                );
                              } else if (rewards.length === 3) {
                                // Three items: card OR card OR card
                                return (
                                  <>
                                    <div className="reward-card">
                                      {rewards[0].image && (
                                        <img 
                                          src={rewards[0].image} 
                                          alt={rewards[0].name || deal.deal_name}
                                          className="reward-image"
                                        />
                                      )}
                                      <div className="reward-name">
                                        {rewards[0].name || deal.deal_name}
                                      </div>
                                      {deal.rng_enabled && rewards[0].chance && (
                                        <div className="reward-chance">
                                          🎲 {rewards[0].chance}% Chance
                                        </div>
                                      )}
                                    </div>
                                    <div className="rng-separator vertical">OR</div>
                                    <div className="reward-card">
                                      {rewards[1].image && (
                                        <img 
                                          src={rewards[1].image} 
                                          alt={rewards[1].name || deal.deal_name}
                                          className="reward-image"
                                        />
                                      )}
                                      <div className="reward-name">
                                        {rewards[1].name || deal.deal_name}
                                      </div>
                                      {deal.rng_enabled && rewards[1].chance && (
                                        <div className="reward-chance">
                                          🎲 {rewards[1].chance}% Chance
                                        </div>
                                      )}
                                    </div>
                                    <div className="rng-separator vertical">OR</div>
                                    <div className="reward-card">
                                      {rewards[2].image && (
                                        <img 
                                          src={rewards[2].image} 
                                          alt={rewards[2].name || deal.deal_name}
                                          className="reward-image"
                                        />
                                      )}
                                      <div className="reward-name">
                                        {rewards[2].name || deal.deal_name}
                                      </div>
                                      {deal.rng_enabled && rewards[2].chance && (
                                        <div className="reward-chance">
                                          🎲 {rewards[2].chance}% Chance
                                        </div>
                                      )}
                                    </div>
                                  </>
                                );
                              } else {
                                // Four or more items: 2x2 grid with separators
                                const elements = [];
                                for (let i = 0; i < rewards.length; i += 2) {
                                  // Add horizontal separator between rows (except before first row)
                                  if (i > 0) {
                                    elements.push(
                                      <div key={`hsep-${i}`} className="rng-separator horizontal">OR</div>
                                    );
                                  }
                                  
                                  // First card of the row
                                  const reward1 = rewards[i];
                                  elements.push(
                                    <div key={i} className="reward-card">
                                      {reward1.image && (
                                        <img 
                                          src={reward1.image} 
                                          alt={reward1.name || deal.deal_name}
                                          className="reward-image"
                                        />
                                      )}
                                      <div className="reward-name">
                                        {reward1.name || deal.deal_name}
                                      </div>
                                      {deal.rng_enabled && reward1.chance && (
                                        <div className="reward-chance">
                                          🎲 {reward1.chance}% Chance
                                        </div>
                                      )}
                                    </div>
                                  );
                                  
                                  // Add vertical separator and second card if it exists
                                  if (i + 1 < rewards.length) {
                                    elements.push(
                                      <div key={`vsep-${i}`} className="rng-separator vertical">OR</div>
                                    );
                                    
                                    const reward2 = rewards[i + 1];
                                    elements.push(
                                      <div key={i + 1} className="reward-card">
                                        {reward2.image && (
                                          <img 
                                            src={reward2.image} 
                                            alt={reward2.name || deal.deal_name}
                                            className="reward-image"
                                          />
                                        )}
                                        <div className="reward-name">
                                          {reward2.name || deal.deal_name}
                                        </div>
                                        {deal.rng_enabled && reward2.chance && (
                                          <div className="reward-chance">
                                            🎲 {reward2.chance}% Chance
                                          </div>
                                        )}
                                      </div>
                                    );
                                  }
                                }
                                return elements;
                              }
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Standalone Recipes (not pinned to any deal) */}
                {config.freeMintRecipes && config.freeMintRecipes.map((recipe, index) => {
                  // Only show recipes that aren't pinned to any deal
                  const mapping = config.recipe_to_deals_mapping || {};
                  const dealIds = mapping[recipe.id] || [];
                  const isPinnedToAnyDeal = dealIds.length > 0;
                  
                  if (isPinnedToAnyDeal) return null; // Skip recipes that are pinned to deals
                  
                  const freeMintInfo = getFreeMintInfo(recipe);
                  const isActualFreeMint = recipe.recipe_type === 'free_mint';
                  const hasTokenGateRequirements = recipe.token_gate_requirements && recipe.token_gate_requirements.length > 0;
                  
                  const totalDealsCount = (config.deals?.length || 0);
                  
                  return (
                    <div key={`recipe-${recipe.id}`} className="deal-card" style={{ animationDelay: `${(totalDealsCount + index) * 0.2}s` }}>
                      <h3 className="deal-name">{recipe.title}</h3>
                      
                      <div className="deal-cost">
                        <span className="cost-label">Mint Cost:</span>
                        <span className="cost-value">
                          {isActualFreeMint ? 'Free Mint' : 'Recipe Burn Required'}
                        </span>
              </div>
                      
                      <div className="deal-limits">
                        <span className="limits-label">Limits:</span>
                        <span className="limits-value">
                          {isActualFreeMint 
                            ? freeMintInfo.allocation 
                            : `${recipe.limit_per_wallet || 'Unlimited'} per wallet${recipe.limit_per_token_enabled ? ` • ${recipe.limit_per_token || 1} per token` : ''}`
                          }
                        </span>
            </div>
                      
                      {/* Show token gate requirements for free mints and token-gated recipes */}
                      {hasTokenGateRequirements && freeMintInfo.requirements.length > 0 && (
                        <div className="free-claims-section">
                          <div className="free-claims-title">
                            {isActualFreeMint ? `Requirements (${recipe.token_gate_logic || 'AND'}):` : `Token Gate Requirements (${recipe.token_gate_logic || 'AND'}):`}
                          </div>
                          <div style={{ 
                            fontSize: '0.75rem', 
                            color: '#ffd700', 
                            textAlign: 'center', 
                            marginBottom: '0.5rem',
                            fontStyle: 'italic'
                          }}>
                            You must hold {(recipe.token_gate_logic || 'AND') === 'OR' ? 'ANY' : 'ALL'} of these items:
                          </div>
                          {freeMintInfo.requirements.map((req, i) => {
                            const marketplaceUrl = getMarketplaceUrl(
                              recipe.token_gate_urn_requirements?.[i]?.collection || 
                              recipe.token_gate_requirements?.[i]?.collection,
                              recipe.token_gate_urn_requirements?.[i]?.urn_item_id || 
                              recipe.token_gate_requirements?.[i]?.item_id
                            );
                            
                            const ItemContent = (
                              <>
                                {req.image && (
                                  <img 
                                    src={req.image} 
                                    alt={req.name}
                                    className="free-claim-image"
                                  />
                                )}
                                <div className="free-claim-info">
                                  <div className="free-claim-name">{req.quantity}x {req.name}</div>
                                  <div className="free-claim-allocation">
                                    {isActualFreeMint ? 'Required to claim' : 'Required to hold'}
                                  </div>
                                </div>
                              </>
                            );

                            return marketplaceUrl ? (
                              <a 
                                key={i} 
                                href={marketplaceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="free-claim-item"
                              >
                                {ItemContent}
                              </a>
                            ) : (
                              <div key={i} className="free-claim-item">
                                {ItemContent}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Show recipe requirements for non-free recipes */}
                      {!isActualFreeMint && (
                        (recipe.burn_urn_requirements && recipe.burn_urn_requirements.length > 0) || 
                        (recipe.requires && recipe.requires.length > 0)
                      ) && (
                        <div className="free-claims-section">
                          <div className="free-claims-title">
                            Burn Requirements ({recipe.logic || 'AND'}):
                          </div>
                          <div style={{ 
                            fontSize: '0.75rem', 
                            color: '#ff6b6b', 
                            textAlign: 'center', 
                            marginBottom: '0.5rem',
                            fontStyle: 'italic'
                          }}>
                            You must burn {(recipe.logic || 'AND') === 'OR' ? 'ANY' : 'ALL'} of these items:
                          </div>
                          {/* Use burn_urn_requirements if available (like token_gate_urn_requirements), otherwise fall back to requires */}
                          {(recipe.burn_urn_requirements && recipe.burn_urn_requirements.length > 0 ? 
                            recipe.burn_urn_requirements : 
                            recipe.requires || []
                          ).map((req, i) => {
                            let reqInfo;
                            if (req.urn_item_id !== undefined) {
                              // This is from burn_urn_requirements - use collection + urn_item_id (like token gates)
                              reqInfo = getCollectionItemInfo(req.collection, req.urn_item_id);
                            } else {
                              // This is from requires - use collection + item_id (old method)
                              reqInfo = getCollectionItemInfo(req.collection, req.item_id);
                            }
                            
                            const marketplaceUrl = getMarketplaceUrl(
                              req.collection,
                              req.urn_item_id || req.item_id
                            );
                            
                            const ItemContent = (
                              <>
                                {reqInfo.image && (
                                  <img 
                                    src={reqInfo.image} 
                                    alt={reqInfo.name}
                                    className="free-claim-image"
                                  />
                                )}
                                <div className="free-claim-info">
                                  <div className="free-claim-name">
                                    {req.quantity}x {reqInfo.name || `Item from ${req.collection.slice(0, 6)}...`}
                                  </div>
                                  <div className="free-claim-allocation" style={{
                                    color: '#ff6b6b'
                                  }}>Required to burn</div>
                                </div>
                              </>
                            );
                            
                            return marketplaceUrl ? (
                              <a 
                                key={i} 
                                href={marketplaceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="free-claim-item" 
                                style={{
                                  background: 'rgba(255, 107, 107, 0.1)',
                                  border: '1px solid rgba(255, 107, 107, 0.3)',
                                  transition: 'all 0.3s ease',
                                  textDecoration: 'none',
                                  color: 'inherit'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = 'rgba(255, 107, 107, 0.15)';
                                  e.target.style.borderColor = 'rgba(255, 107, 107, 0.5)';
                                  e.target.style.transform = 'translateX(5px)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = 'rgba(255, 107, 107, 0.1)';
                                  e.target.style.borderColor = 'rgba(255, 107, 107, 0.3)';
                                  e.target.style.transform = 'translateX(0)';
                                }}
                              >
                                {ItemContent}
                              </a>
                            ) : (
                              <div 
                                key={i} 
                                className="free-claim-item" 
                                style={{
                                  background: 'rgba(255, 107, 107, 0.1)',
                                  border: '1px solid rgba(255, 107, 107, 0.3)',
                                  transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = 'rgba(255, 107, 107, 0.15)';
                                  e.target.style.borderColor = 'rgba(255, 107, 107, 0.5)';
                                  e.target.style.transform = 'translateX(5px)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = 'rgba(255, 107, 107, 0.1)';
                                  e.target.style.borderColor = 'rgba(255, 107, 107, 0.3)';
                                  e.target.style.transform = 'translateX(0)';
                                }}
                              >
                                {ItemContent}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                      {/* Show the recipe reward(s) */}
                      <div className="rewards-section">
                        <div className="rewards-title">
                          {isActualFreeMint ? 
                            (recipe.rng_enabled ? 'Possible Free Rewards:' : 'Free Mint Item:') : 
                            (recipe.rng_enabled ? 'Possible Rewards:' : 'Recipe Reward:')
                          }
                        </div>
                        {(() => {
                          const rewards = getRewards(recipe);
                          if (rewards.length === 0) return null;
                          
                          return (
                            <div className={`rewards-grid ${
                              rewards.length === 1 ? 'single-item' :
                              recipe.rng_enabled ? 'rng-quad-layout' :
                              rewards.length === 2 ? 'two-items' :
                              rewards.length === 3 ? 'three-items' :
                              'four-plus-items'
                            }`}>
                              {(() => {
                                if (recipe.rng_enabled && rewards.length > 1) {
                                  if (rewards.length >= 4) {
                                    // Quad layout for 4+ rewards
                                    return (
                                      <div>
                                        {/* Top row */}
                                        <div className="rng-quad-row">
                                          <div className="rng-quad-item">
                                            {rewards[0].image && (
                                              <img 
                                                src={rewards[0].image} 
                                                alt={rewards[0].name}
                                                className="reward-image"
                                              />
                                            )}
                                            <div className="reward-name">{rewards[0].name}</div>
                                            <div className="reward-chance">
                                              🎲 {rewards[0].chance}% Chance
                                            </div>
                                          </div>
                                          
                                          <div className="rng-quad-or">OR</div>
                                          
                                          <div className="rng-quad-item">
                                            {rewards[1].image && (
                                              <img 
                                                src={rewards[1].image} 
                                                alt={rewards[1].name}
                                                className="reward-image"
                                              />
                                            )}
                                            <div className="reward-name">{rewards[1].name}</div>
                                            <div className="reward-chance">
                                              🎲 {rewards[1].chance}% Chance
                                            </div>
                                          </div>
                                        </div>
                                        
                                        {/* ORs above bottom row */}
                                        <div style={{ 
                                          display: 'flex', 
                                          justifyContent: 'space-around', 
                                          alignItems: 'center',
                                          margin: '1rem 0',
                                          position: 'relative'
                                        }}>
                                          <div style={{ flex: 1, textAlign: 'center' }}>
                                            <div className="rng-quad-or">OR</div>
                                          </div>
                                          <div style={{ flex: 1, textAlign: 'center' }}>
                                            <div className="rng-quad-or">OR</div>
                                          </div>
                                        </div>
                                        
                                        {/* Bottom row */}
                                        <div className="rng-quad-row">
                                          <div className="rng-quad-item">
                                            {rewards[2].image && (
                                              <img 
                                                src={rewards[2].image} 
                                                alt={rewards[2].name}
                                                className="reward-image"
                                              />
                                            )}
                                            <div className="reward-name">{rewards[2].name}</div>
                                            <div className="reward-chance">
                                              🎲 {rewards[2].chance}% Chance
                                            </div>
                                          </div>
                                          
                                          <div className="rng-quad-or">OR</div>
                                          
                                          <div className="rng-quad-item">
                                            {rewards[3].image && (
                                              <img 
                                                src={rewards[3].image} 
                                                alt={rewards[3].name}
                                                className="reward-image"
                                              />
                                            )}
                                            <div className="reward-name">{rewards[3].name}</div>
                                            <div className="reward-chance">
                                              🎲 {rewards[3].chance}% Chance
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  } else if (rewards.length === 2) {
                                    // Two items: quad layout style
                                    return (
                                      <div>
                                        {/* Single row with two items */}
                                        <div className="rng-quad-row">
                                          <div className="rng-quad-item">
                                            {rewards[0].image && (
                                              <img 
                                                src={rewards[0].image} 
                                                alt={rewards[0].name}
                                                className="reward-image"
                                                style={{
                                                  width: '70px',
                                                  height: '70px'
                                                }}
                                              />
                                            )}
                                            <div className="reward-name" style={{ fontSize: '0.9rem' }}>
                                              {rewards[0].name}
                                            </div>
                                            <div className="reward-chance" style={{ 
                                              fontSize: '0.65rem', 
                                              minWidth: '70px',
                                              whiteSpace: 'nowrap',
                                              flexShrink: 0,
                                              padding: '0.3rem 0.6rem'
                                            }}>
                                              🎲 {rewards[0].chance}% Chance
                                            </div>
                                          </div>
                                          
                                          <div className="rng-quad-or">OR</div>
                                          
                                          <div className="rng-quad-item">
                                            {rewards[1].image && (
                                              <img 
                                                src={rewards[1].image} 
                                                alt={rewards[1].name}
                                                className="reward-image"
                                                style={{
                                                  width: '70px',
                                                  height: '70px'
                                                }}
                                              />
                                            )}
                                            <div className="reward-name" style={{ fontSize: '0.9rem' }}>
                                              {rewards[1].name}
                                            </div>
                                            <div className="reward-chance" style={{ 
                                              fontSize: '0.65rem', 
                                              minWidth: '70px',
                                              whiteSpace: 'nowrap',
                                              flexShrink: 0,
                                              padding: '0.3rem 0.6rem'
                                            }}>
                                              🎲 {rewards[1].chance}% Chance
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  } else if (rewards.length === 3) {
                                    // Vertical list for 3 rewards only
                                    const elements = [];
                                    rewards.forEach((reward, i) => {
                                    // Add reward tile
                                    elements.push(
                                      <div key={i} className="reward-tile-full">
                                        {reward.image && (
                                          <img 
                                            src={reward.image} 
                                            alt={reward.name}
                                            className="reward-tile-image"
                                          />
                                        )}
                                        <div className="reward-tile-content">
                                          <div className="reward-tile-title-row">
                                            <div className="reward-name">{reward.name}</div>
                                            <div className="reward-chance">
                                              🎲 {reward.chance}% Chance
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                    
                                    // Add OR separator (except after last item)
                                    if (i < rewards.length - 1) {
                                      elements.push(
                                        <div key={`or-${i}`} className="rng-separator-vertical">OR</div>
                                      );
                                    }
                                    });
                                    return elements;
                                  }
                                } else if (rewards.length === 1) {
                                  // Single item - no separators needed
                                  const reward = rewards[0];
                                  return (
                                    <div className="reward-card">
                                      {reward.image && (
                                        <img 
                                          src={reward.image} 
                                          alt={reward.name}
                                          className="reward-image"
                                        />
                                      )}
                                      <div className="reward-name">{reward.name}</div>
                                      {recipe.rng_enabled && reward.chance && (
                                        <div className="reward-chance">
                                          🎲 {reward.chance}% Chance
                                        </div>
                                      )}
                                    </div>
                                  );
                                } else if (rewards.length === 3) {
                                  // Three items: card OR card OR card
                                  return (
                                    <>
                                      <div className="reward-card">
                                        {rewards[0].image && (
                                          <img 
                                            src={rewards[0].image} 
                                            alt={rewards[0].name}
                                            className="reward-image"
                                          />
                                        )}
                                        <div className="reward-name">{rewards[0].name}</div>
                                        {recipe.rng_enabled && rewards[0].chance && (
                                          <div className="reward-chance">
                                            🎲 {rewards[0].chance}% Chance
                                          </div>
                                        )}
                                      </div>
                                      <div className="rng-separator vertical">OR</div>
                                      <div className="reward-card">
                                        {rewards[1].image && (
                                          <img 
                                            src={rewards[1].image} 
                                            alt={rewards[1].name}
                                            className="reward-image"
                                          />
                                        )}
                                        <div className="reward-name">{rewards[1].name}</div>
                                        {recipe.rng_enabled && rewards[1].chance && (
                                          <div className="reward-chance">
                                            🎲 {rewards[1].chance}% Chance
                                          </div>
                                        )}
                                      </div>
                                      <div className="rng-separator vertical">OR</div>
                                      <div className="reward-card">
                                        {rewards[2].image && (
                                          <img 
                                            src={rewards[2].image} 
                                            alt={rewards[2].name}
                                            className="reward-image"
                                          />
                                        )}
                                        <div className="reward-name">{rewards[2].name}</div>
                                        {recipe.rng_enabled && rewards[2].chance && (
                                          <div className="reward-chance">
                                            🎲 {rewards[2].chance}% Chance
                                          </div>
                                        )}
                                      </div>
                                    </>
                                  );
                                } else {
                                  // Four or more items: 2x2 grid with separators
                                  const elements = [];
                                  for (let i = 0; i < rewards.length; i += 2) {
                                    // Add horizontal separator between rows (except before first row)
                                    if (i > 0) {
                                      elements.push(
                                        <div key={`hsep-${i}`} className="rng-separator horizontal">OR</div>
                                      );
                                    }
                                    
                                    // First card of the row
                                    const reward1 = rewards[i];
                                    elements.push(
                                      <div key={i} className="reward-card">
                                        {reward1.image && (
                                          <img 
                                            src={reward1.image} 
                                            alt={reward1.name}
                                            className="reward-image"
                                          />
                                        )}
                                        <div className="reward-name">{reward1.name}</div>
                                        {recipe.rng_enabled && reward1.chance && (
                                          <div className="reward-chance">
                                            🎲 {reward1.chance}% Chance
                                          </div>
                                        )}
                                      </div>
                                    );
                                    
                                    // Add vertical separator and second card if it exists
                                    if (i + 1 < rewards.length) {
                                      elements.push(
                                        <div key={`vsep-${i}`} className="rng-separator vertical">OR</div>
                                      );
                                      
                                      const reward2 = rewards[i + 1];
                                      elements.push(
                                        <div key={i + 1} className="reward-card">
                                          {reward2.image && (
                                            <img 
                                              src={reward2.image} 
                                              alt={reward2.name}
                                              className="reward-image"
                                            />
                                          )}
                                          <div className="reward-name">{reward2.name}</div>
                                          {recipe.rng_enabled && reward2.chance && (
                                            <div className="reward-chance">
                                              🎲 {reward2.chance}% Chance
                                            </div>
                                          )}
                                        </div>
                                      );
                                    }
                                  }
                                  return elements;
                                }
                              })()}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                }).filter(Boolean)}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mint-launching-footer">
            <p>Made with ♥ by</p>
            <div className="footer-logo">DOCTORdripp</div>
          </div>
        </div>
      </div>
      
      {/* Launch Live Overlay - appears during page transition */}
      {isLaunchComplete && (
        <div className="launch-live-overlay">
          <div className="launch-live-message" style={{
            fontSize: 'clamp(3rem, 8vw, 6rem)',
            fontWeight: '900',
            background: 'linear-gradient(45deg, #ff0000, #ff8c00, #ffd700, #32cd32, #00bfff, #8a2be2, #ff1493)',
            backgroundSize: '400% 400%',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textAlign: 'center',
            padding: '3rem',
            borderRadius: '30px',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '3px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 0 50px rgba(255, 255, 255, 0.3), inset 0 0 30px rgba(255, 255, 255, 0.1)',
            textShadow: '0 0 30px rgba(255, 255, 255, 0.5)'
          }}>
MINT IS LIVE
          </div>
        </div>
      )}
    </>
  );
}

export default MintLaunchingPage;
